import { useMemo, useState, useEffect } from "react";
import { useEvent } from "../contexts/EventContext";
import { EventSelectorBar } from "../components/organisms/EventSelectorBar.jsx";
import { VisitorTable } from "../components/molecules/VisitorTable.jsx";
import { useVisitors } from "../hooks/useVisitors.js";
import { PageHeader } from "../components/organisms/PageHeader.jsx";
import { VisitorListControlsPanel } from "../components/organisms/VisitorListControlsPanel.jsx";
import { getVisitStatus } from "../lib/visitStatus";

export const ListPage = () => {
  const { rows, loading, error, search, clear } = useVisitors();
  const { selectedEvent } = useEvent();

  const [selectedSeminarId, setSelectedSeminarId] = useState("");
  const seminars = selectedEvent?.seminars ?? [];

  // フィルター追加
  const [visitorNameFilter, setVisitorNameFilter] = useState("");
  const [companyNameFilter, setCompanyNameFilter] = useState("");
  const [addressFilter, setAddressFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState([ "ready", "checked", "late", ]);

  useEffect(() => {
    setSelectedSeminarId("");
    clear();
  }, [selectedEvent?.id, clear]);

  // 選択中イベントの日付を"〇月〇日"形式に変換
  const selectedEventDateLabel = useMemo(() => {
    if (!selectedEvent?.date) return "";

    const parts = selectedEvent.date.split("-");
    if (parts.length !== 3) return "";

    const month = parts[1];
    const day = parts[2];

    return `${Number(month)}月${Number(day)}日`;
  }, [selectedEvent?.date]);

  // まず日付で絞る
  const dateFilteredRows = useMemo(() => {
    if (!selectedEventDateLabel) return rows ?? [];

    return (rows ?? []).filter((r) => {
      // そもそも受付済みなら表示する
      if (r.visited) {
        return true;
      }

      const value = r.preferred_visit_date;

      // 配列の場合
      if (Array.isArray(value)) {
        return value.some((item) =>
          String(item ?? "").includes(selectedEventDateLabel)
      );
    }

    // 文字列の場合
    return String(value ?? "").includes(selectedEventDateLabel);
    });
  }, [rows, selectedEventDateLabel]);

  // 選択されたセミナーIDから表示用のセミナーIDを取得する処理
  const seminarLabel = useMemo(() => {
    if (!selectedSeminarId) return "";
    return seminars.find((s) => s.id === selectedSeminarId)?.label ?? "";
  }, [selectedSeminarId, seminars]);

  // 各フィルター処理
  const filteredRows = useMemo(() => {
    return (dateFilteredRows ?? []).filter((r) => {
      // セミナー
      const seminarMatch =
        !seminarLabel ||
        String(r.applied_seminars ?? "").includes(seminarLabel);

      // 氏名
      const nameMatch =
        !visitorNameFilter ||
        String(r.visitor_name ?? "").includes(visitorNameFilter);

      // 会社名
      const companyMatch =
        !companyNameFilter ||
        String(r.company_name ?? "").includes(companyNameFilter);

      // 住所
      const addressMatch =
        !addressFilter ||
        String([r.prefecture, r.municipality, r.address, r.building_name].join("") ?? "").includes(addressFilter)

      // 業種
      const industryMatch =
        !industryFilter ||
        r.industry === industryFilter;

      // 受付状態      
      const rowStatus = getVisitStatus({
        preferredVisitText: r.preferred_visit_date,
        targetDate: selectedEvent?.date,
        visited: r.visited,
      });
      const statusMatch = statusFilter.length === 0 || statusFilter.includes(rowStatus);

      return (
        seminarMatch &&
        nameMatch &&
        companyMatch &&
        addressMatch &&
        industryMatch &&
        statusMatch
      );
    });
  }, [
    dateFilteredRows,
    seminarLabel,
    visitorNameFilter,
    companyNameFilter,
    addressFilter,
    industryFilter,
    statusFilter,
  ]);

  const mapped = filteredRows;

  const progress = useMemo(() => {
    const total = mapped.length;
    const checked = mapped.filter((r) => r.visited === true).length;
    const subUserList = [
      "設計事務所",
      "ハウスメーカー",
      "キッチンメーカー",
      "リフォーム・リノベーション",
      "工務店・ビルダー",
      "マンション管理",
      "サブコン・設備",
      "デベロッパー",
      "ゼネコン",
    ]
    const subUserTotal = mapped.filter((r) => subUserList.includes(r.industry)).length;
    const subUserChecked = mapped.filter((r) => subUserList.includes(r.industry) && r.visited === true).length;
    return { total, checked, subUserTotal, subUserChecked };
  }, [mapped]);

  return (
    <>
      <PageHeader
        title="来場予定・状況一覧" 
        description={
          <>
          受付手順<br />
          1.イベントの選択<br />
          2.検索ボタンをタップ<br />
          3.詳細情報を閲覧したい場合は該当のお客様の行をタップ<br />
          4.検索結果に条件付けして表示したい場合は条件を選択する（最新の情報を出力したい場合は検索ボタンをタップしてください）<br />
          <br />
          ※処理に少しお時間かかる場合がございますので5~10秒ほどお待ちください
          </>
        }
      />
      <EventSelectorBar />
      
      <VisitorListControlsPanel
        total={progress.total}
        checked={progress.checked}
        subUserTotal={progress.subUserTotal}
        subUserChecked={progress.subUserChecked}
        loading={loading}
        onSearch={search}

        seminars={seminars}

        visitorName={visitorNameFilter}
        onChangeVisitorName={setVisitorNameFilter}

        companyName={companyNameFilter}
        onChangeCompanyName={setCompanyNameFilter}

        address={addressFilter}
        onChangeAddress={setAddressFilter}

        industry={industryFilter}
        onChangeIndustry={setIndustryFilter}

        status={statusFilter}
        onChangeStatus={setStatusFilter}

        selectedSeminarId={selectedSeminarId}
        onChangeSeminar={setSelectedSeminarId}
      />

      <VisitorTable rows={mapped} />
    </>
  )
}